import { useUpdateMyPresence } from "@liveblocks/react";
import { useCallback, useEffect, useRef } from "react";
import { useMouseRef, useScrollRef } from "../../jotai/atoms";
import { Point2D } from "../../types";
import { canvasPos } from "../../utils/canvasPos";
import { addPosition } from "../../utils/gridPosition";
import useThrottle from "../../utils/useThrottle";
import { useWindowSize } from "../../utils/useWindowSize";

export function useCursorUpdater() {
  const windowSize = useWindowSize();
  const updateMyPresence = useUpdateMyPresence();
  const myPresenceLastUpdatedAt = useRef(0);
  const cursorPosRef = useMouseRef();
  const clientX = useRef(0);
  const clientY = useRef(0);
  const scroll = useScrollRef();

  const updateCursor = useCallback(
    (pos: Point2D, cursor?: string) => {
      if (!cursorPosRef) {
        return;
      }

      cursorPosRef.current.x = pos.x;
      cursorPosRef.current.y = pos.y;

      if (Date.now() - myPresenceLastUpdatedAt.current > 10) {
        if (cursor) {
          updateMyPresence({ position: pos, cursor }); // expensive work
        } else {
          updateMyPresence({ position: pos }); // expensive work
        }
        myPresenceLastUpdatedAt.current = Date.now();
      }
    },
    [cursorPosRef, updateMyPresence]
  );

  function onMouseMove(e: MouseEvent) {
    const pos = canvasPos(e);
    let cursor = undefined;

    if (e.target instanceof Element) {
      cursor = findCursorType(e.target);
    }

    updateCursor(pos, cursor);

    // store these values so we know where the mouse is, useful for scroll.
    clientX.current = e.clientX;
    clientY.current = e.clientY;
  }
  // it's important to throttle this, because sometimes mousemove and dragover both fire
  const onMouseMoveThrottled = useThrottle(onMouseMove, 1000 / 60);

  // Manage current user mouse position in canvas space
  useEffect(() => {
    if (!cursorPosRef) return;

    function onScroll() {
      if (!scroll?.current) return;

      const pos = addPosition(
        { x: clientX.current, y: clientY.current },
        scroll.current
      );
      updateCursor(canvasPos({ pageX: pos.x, pageY: pos.y }));
    }

    // use window instead of canvasRef here so cursor is always
    // present and doesn't disappear when hovering over chrome
    // or any children
    window.addEventListener("mousemove", onMouseMoveThrottled);
    window.addEventListener("dragover", onMouseMoveThrottled);
    window.addEventListener("scroll", onScroll);
    return () => {
      window.removeEventListener("mousemove", onMouseMoveThrottled);
      window.removeEventListener("dragover", onMouseMoveThrottled);
      window.removeEventListener("scroll", onScroll);
    };
  }, [cursorPosRef, scroll, windowSize, onMouseMoveThrottled, updateCursor]);
}

function findDataAttrRecursively(
  element: Element,
  attribute: string
): string | undefined {
  const ancestor = element.closest(`[${attribute}]`);

  const attr_val = ancestor?.getAttribute(attribute) ?? undefined;
  return attr_val;
}

export function findCursorType(element: Element): string | undefined {
  const tag = element.tagName.toLowerCase();
  if (
    tag === "textarea" ||
    (tag === "input " && element.getAttribute("type")?.toLowerCase() === "text")
  ) {
    return "text";
  }
  if (tag === "button") {
    return "pointer";
  }

  const cursor = findDataAttrRecursively(element, "data-cursor");
  return cursor;
}
